'use client'

import { useEffect, useState } from 'react'
import { logClientError } from '@/lib/logError'
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/chunkError'
import { isTransientFetchError, clientDiagnostics } from '@/lib/clientErrors'

// global-error.tsx replaces the root layout entirely, so it must render
// its own <html> and <body>. Tailwind isn't available here — use inline styles.
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const chunkError = isChunkLoadError(error)
  // Same split as app/error.tsx, and it has to be repeated rather than shared: this boundary
  // renders its own document with no Tailwind, so only the classification is common (NEA-233).
  const offline = !chunkError && isTransientFetchError(error)
  const [recovering, setRecovering] = useState(false)

  useEffect(() => {
    logClientError({
      source: 'error_boundary',
      message: error.message,
      stack: error.stack,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      metadata: { digest: error.digest, scope: 'global', chunk: chunkError, ...clientDiagnostics() },
    })
    if (chunkError && reloadOnceForChunkError()) setRecovering(true)
  }, [error, chunkError])

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: 'sans-serif', display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        {recovering ? (
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>Updating to the latest version…</p>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: 0 }}>
              {offline ? 'Couldn’t reach ppleague' : 'Something went wrong'}
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
              {offline
                ? 'The connection dropped before the page finished loading. Check your signal and try again — nothing was lost.'
                : 'An unexpected error occurred. The issue has been logged.'}
            </p>
            <button
              onClick={reset}
              style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}
            >
              Try again
            </button>
          </div>
        )}
      </body>
    </html>
  )
}
